import { motion } from "framer-motion";
import { Zap, AlertTriangle, Users, TrendingDown, DollarSign, Clock, Target, ArrowRight } from "lucide-react";

const SIGNALS = [
  {
    icon: Zap,
    dot: "bg-emerald-400",
    glow: "rgba(52,211,153,0.15)",
    label: "Buying Signal",
    text: "Prospect confirms budget and timeline alignment",
    live: true,
  },
  {
    icon: AlertTriangle,
    dot: "bg-amber-400",
    glow: null,
    label: "Objection Detected",
    text: "Implementation complexity raised as a concern",
    live: false,
  },
  {
    icon: Users,
    dot: "bg-blue-400",
    glow: "rgba(96,165,250,0.15)",
    label: "Stakeholder Change",
    text: "New decision maker joined — adjust your narrative",
    live: true,
  },
  {
    icon: TrendingDown,
    dot: "bg-red-400",
    glow: null,
    label: "Risk to Close",
    text: "Competitor mentioned twice — differentiation needed",
    live: false,
  },
  {
    icon: DollarSign,
    dot: "bg-violet-400",
    glow: null,
    label: "Pricing Concern",
    text: "Cost justification requested — prepare ROI data",
    live: false,
  },
  {
    icon: Clock,
    dot: "bg-amber-400",
    glow: "rgba(251,191,36,0.12)",
    label: "Urgency Signal",
    text: "Hard deadline confirmed for end of quarter",
    live: true,
  },
  {
    icon: Target,
    dot: "bg-teal-400",
    glow: null,
    label: "Next Step",
    text: "Trial or pilot discussed — suggest specific dates",
    live: false,
  },
  {
    icon: ArrowRight,
    dot: "bg-sky-400",
    glow: null,
    label: "Decision Criteria",
    text: "Security and compliance flagged as primary gates",
    live: false,
  },
];

function SignalCard({ signal, index }) {
  const Icon = signal.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: index * 0.055, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-xl border border-white/[0.08] bg-white/[0.04] p-5 overflow-hidden"
      style={signal.glow ? { boxShadow: `inset 0 0 40px 0 ${signal.glow}` } : undefined}
    >
      {/* Live indicator */}
      {signal.live && (
        <span className="absolute top-4 right-4 flex h-1.5 w-1.5">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-70 ${signal.dot}`} />
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${signal.dot}`} />
        </span>
      )}

      {/* Icon */}
      <div className="mb-4 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.06] border border-white/[0.08]">
        <Icon className="h-3.5 w-3.5 text-white/75" strokeWidth={2} />
      </div>

      {/* Label row */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${signal.dot}`} />
        <span className="text-[10.5px] font-semibold uppercase tracking-widest text-white/40">
          {signal.label}
        </span>
      </div>

      {/* Description */}
      <p className="text-[13px] leading-snug text-white/75">{signal.text}</p>
    </motion.div>
  );
}

export default function RealtimeIntelSection() {
  return (
    <section id="intelligence" className="relative overflow-hidden bg-[#070D1A] py-20 sm:py-28">
      {/* Subtle dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* Atmospheric glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0092B4]/[0.12] blur-[120px]" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center mb-14">
          <h2 className="text-[clamp(1.8rem,4vw,2.85rem)] font-bold tracking-tight text-white">
            Shadow listens for what{" "}
            <span className="text-[#0092B4]">moves deals.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto">
            Most reps hear these signals. Shadow acts on them — in under a second.
          </p>
        </div>

        {/* Signal cards grid */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SIGNALS.map((signal, i) => (
            <SignalCard key={signal.label} signal={signal} index={i} />
          ))}
        </div>

        {/* Stats bar */}
        <div className="mt-10 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-6 py-7 sm:px-10">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/[0.08]">
            {[
              { value: "< 1s",  label: "Signal detection latency"      },
              { value: "60+",   label: "Languages supported"            },
              { value: "Live",  label: "Context from CRM and past calls" },
            ].map(stat => (
              <div key={stat.label} className="text-center py-4 sm:py-0">
                <p className="text-[2.4rem] font-bold tracking-tight text-white">{stat.value}</p>
                <p className="mt-1 text-[13px] text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
